import { useCallback, useEffect } from "react";

import { useStore } from "@/store/store";

import { type TColumnDef, type TDensity } from "../types";

export type TUseGridPreferencesResult = {
  columnVisibility: Record<string, boolean>;
  columnOrder: string[];
  density: TDensity;
  pageSize: number;
  setColumnVisibility: (visibility: Record<string, boolean>) => void;
  setColumnOrder: (order: string[]) => void;
  setDensity: (density: TDensity) => void;
  setPageSize: (pageSize: number) => void;
};

/**
 * Reads/writes a grid's persisted preferences (column visibility, order,
 * density, page size) from the localStorage-backed Zustand slice, keyed by
 * `persistKey`. Column defaults come from the column definitions.
 */
export function useGridPreferences<TRow>(
  persistKey: string,
  columns: TColumnDef<TRow>[],
  defaultPageSize: number
): TUseGridPreferencesResult {
  const hydrate = useStore.use.hydrateDataGridPrefs();
  const prefs = useStore.use.gridPreferences()[persistKey];
  const setVisibility = useStore.use.setColumnVisibility();
  const setOrder = useStore.use.setColumnOrder();
  const setDensityAction = useStore.use.setDensity();
  const setPageSizeAction = useStore.use.setGridPageSize();

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  // Column visibility: merge stored overrides over declaration defaults so new
  // columns show up automatically.
  const defaultVisibility: Record<string, boolean> = {};
  for (const column of columns) {
    defaultVisibility[column.id] = !column.defaultHidden;
  }
  const columnVisibility = { ...defaultVisibility, ...(prefs?.columnVisibility ?? {}) };

  const columnOrder = prefs?.columnOrder?.length ? prefs.columnOrder : columns.map((c) => c.id);
  const density: TDensity = prefs?.density ?? "comfortable";
  const pageSize = prefs?.pageSize ?? defaultPageSize;

  const setColumnVisibility = useCallback(
    (visibility: Record<string, boolean>) => setVisibility(persistKey, visibility),
    [setVisibility, persistKey]
  );
  const setColumnOrder = useCallback(
    (order: string[]) => setOrder(persistKey, order),
    [setOrder, persistKey]
  );
  const setDensity = useCallback(
    (value: TDensity) => setDensityAction(persistKey, value),
    [setDensityAction, persistKey]
  );
  const setPageSize = useCallback(
    (value: number) => setPageSizeAction(persistKey, value),
    [setPageSizeAction, persistKey]
  );

  return {
    columnVisibility,
    columnOrder,
    density,
    pageSize,
    setColumnVisibility,
    setColumnOrder,
    setDensity,
    setPageSize,
  };
}
